import { NoteRepository } from '../data/db'

/**
 * The sign-in strip (docs/onboarding, DEC-25, DEC-26): a guest who has saved a first note sees one dismissible line in Browse that says
 * sign-in is optional and what it enables. If it was dismissed before the person had `resurfaceAtNotes` notes, it comes back once when they
 * reach that many; dismissed again (or dismissed at or after that many), it is gone for good. At most one strip shows at a time: this is a
 * single decision, and Browse renders one component for it.
 * **Only a brand-new workspace gets it** (owner, DEC-28): one this device saw empty, with the welcome showing. A guest who already had notes when
 * this arrived never sees it, nor one who empties their notes later (the welcome's own flag ends the welcome for good, so it cannot show again).
 * `dismissals` holds the note count at each dismissal, oldest first.
 */
export interface SignInStripState { guest: boolean; brandNew: boolean; inventoryReady: boolean; noteCount: number; dismissals: readonly number[]; resurfaceAtNotes: number }

export function shouldShowSignInStrip(state: SignInStripState): boolean {
  if (!state.guest || !state.brandNew || !state.inventoryReady || state.noteCount < 1) return false
  if (state.dismissals.length === 0) return true
  const [first] = state.dismissals
  return state.dismissals.length === 1 && first! < state.resurfaceAtNotes && state.noteCount >= state.resurfaceAtNotes
}

/** The dismissals are a local row in the workspace's `meta` store per scope, like the welcome's flag (welcomeRules.ts). */
export const stripKey = (scope: string) => `signin-strip-dismissed:v1:${scope}`

/** Stored as the note counts joined by commas ("1,5"); an earlier build stored a plain `true`, which is one dismissal, at the first note. */
export async function readStripDismissals(scope: string, repository = new NoteRepository()): Promise<number[]> {
  const value = await repository.getMeta(stripKey(scope))
  if (value === true) return [1]
  if (typeof value !== 'string' || !value) return []
  return value.split(',').map(Number).filter(count => Number.isInteger(count) && count >= 0)
}

export async function writeStripDismissals(scope: string, dismissals: readonly number[], repository = new NoteRepository()): Promise<void> {
  await repository.setMeta(stripKey(scope), dismissals.join(','))
}

/** Whether this device saw the scope as a brand-new workspace (the welcome showed): a local `meta` row per scope. */
export const eligibleKey = (scope: string) => `signin-strip-eligible:v1:${scope}`

export async function readStripEligible(scope: string, repository = new NoteRepository()): Promise<boolean> {
  return (await repository.getMeta(eligibleKey(scope))) === true
}

export async function writeStripEligible(scope: string, repository = new NoteRepository()): Promise<void> {
  await repository.setMeta(eligibleKey(scope), true)
}
